import { useMemo } from "react";
import { AdditionalFeature, Product, ProductShelvesSection, ProductsQueryParams } from "@core/types";
import { useGetProductsInfiniteQuery } from "../../../providers/products";
import { useActiveOrMainShippingZone } from "../../../providers/shippingZones";

const PRODUCT_SHELF_PAGE_SIZE = "12";

function getShelfQueryParams(
  shelf: ProductShelvesSection,
  zoneId: string,
  additionalProductFilter?: string,
): ProductsQueryParams {
  const tag = shelf.tag;

  const params: ProductsQueryParams = {
    zoneId,
    additionalProductFilter,
    pageNumber: "1",
    pageSize: PRODUCT_SHELF_PAGE_SIZE,
    categoryId: shelf.categoryId || undefined,
    productIds: shelf.productIds?.length ? shelf.productIds : undefined,
  };

  if (tag === "PROFITABLE") {
    params.saleType = true as unknown as ProductsQueryParams["saleType"];
  }

  if (tag === "GATHERED_FOR_YOU") {
    params.featured = true;
  }

  return params;
}

type UseProductShelfProductsProps = {
  additionalProductFilter?: string;
  shelf: ProductShelvesSection | null | undefined;
};

export function useProductShelfProducts({ shelf, additionalProductFilter }: UseProductShelfProductsProps) {
  const { activeShippingZone, isLoading: isActiveShippingZoneLoading } = useActiveOrMainShippingZone();
  const zoneId = activeShippingZone.id;
  const isQueryEnabled = !!zoneId && !!shelf;

  const queryParams = useMemo(
    () => (shelf ? getShelfQueryParams(shelf, zoneId, additionalProductFilter) : { zoneId }),
    [shelf, zoneId, additionalProductFilter],
  );

  const {
    data: fetchedProducts,
    fetchNextPage,
    hasNextPage,
    isLoading: isProductsLoading,
    isFetchingNextPage,
  } = useGetProductsInfiniteQuery({
    options: { enabled: isQueryEnabled },
    params: queryParams,
  });

  const products = useMemo(() => {
    const productsMap = new Map<string | number, Product>();

    fetchedProducts?.pages.forEach((page) => {
      page?.products?.forEach((product) => {
        if (product && !productsMap.has(product.id)) {
          productsMap.set(product.id, product);
        }
      });
    });

    return Array.from(productsMap.values());
  }, [fetchedProducts]);

  const additionalFilters = useMemo(() => {
    const filtersMap = new Map<string | number, AdditionalFeature>();
    const additionalFeatures = fetchedProducts?.pages.flatMap((page) => page?.additionalFeatures) ?? [];

    additionalFeatures.forEach((additionalFeature) => {
      if (additionalFeature && !filtersMap.has(additionalFeature.additionalFeature)) {
        filtersMap.set(additionalFeature.additionalFeature, additionalFeature);
      }
    });

    return Array.from(filtersMap.values());
  }, [fetchedProducts]);

  const isLoading = isActiveShippingZoneLoading || (isQueryEnabled && isProductsLoading);

  return {
    products,
    additionalFilters,
    isLoading,
    isInfiniteQueryEnabled: isQueryEnabled,
    hasNextPage: isQueryEnabled && !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  };
}
